import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  ArrowLeft,
  Container,
  Server,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  AlertCircle,
  Cpu,
  HardDrive,
  Square,
  Play,
  Maximize2,
  Trash2,
  Move,
  Globe,
} from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';
import type { TenantContainer, RuntimeInContainer, ContainerStats, ContainerHistory, HistoryPoint, ContainerDiskUsage } from '../types/tenantContainer';

/** 从 axios 错误中提取后端 detail 文案 */
const getErrorDetail = (e: unknown, fallback: string): string => {
  const err = e as { response?: { data?: { detail?: unknown } } };
  const detail = err.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

/** 从 axios 错误中提取 HTTP 状态码 */
const getErrorStatus = (e: unknown): number | undefined => {
  const err = e as { response?: { status?: number } };
  return err.response?.status;
};

interface DashboardRuntime {
  id: string;
  framework: string;
  status: string;
  sandbox_id: string | null;
  container_id: string | null;
  port: number | null;
  endpoint_url: string | null;
  deployed_at: string | null;
  last_heartbeat: string | null;
  runtime_info: Record<string, unknown> | null;
}

interface DashboardAgent {
  id: string;
  name: string;
  domain: string | null;
  status: string;
  active_profile_id: string | null;
  runtimes: DashboardRuntime[];
  runtime_count: number;
  created_at: string;
}

interface DashboardData {
  agents: DashboardAgent[];
  total_agents: number;
  status_counts: Record<string, number>;
  framework_counts: Record<string, number>;
  total_runtimes: number;
  running_runtimes: number;
  stopped_runtimes: number;
  error_runtimes: number;
}

interface ScaleForm {
  max_agents: number;
  cpu: string;
  memory_mb: string;
}

export default function InstanceOverview() {
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [containers, setContainers] = useState<TenantContainer[]>([]);
  const [containerStats, setContainerStats] = useState<Record<string, ContainerStats>>({});
  const [containerDiskUsage, setContainerDiskUsage] = useState<Record<string, ContainerDiskUsage>>({});
  const [containerHistory, setContainerHistory] = useState<Record<string, ContainerHistory>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scaleTarget, setScaleTarget] = useState<TenantContainer | null>(null);
  const [scaleForm, setScaleForm] = useState<ScaleForm>({
    max_agents: 0,
    cpu: '',
    memory_mb: '',
  });
  const [scaling, setScaling] = useState(false);

  const [migrateRuntime, setMigrateRuntime] = useState<RuntimeInContainer | null>(null);
  const [migrateSourceContainerId, setMigrateSourceContainerId] = useState<string | null>(null);
  const [migrateTargetId, setMigrateTargetId] = useState<string>('');
  const [migrating, setMigrating] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [crossTenant, setCrossTenant] = useState(false);
  const [allContainers, setAllContainers] = useState<TenantContainer[] | null>(null);
  const [loadingAllContainers, setLoadingAllContainers] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashRes, tcRes] = await Promise.all([
        apiClient.get('/dashboard/agents'),
        apiClient.get('/tenant-containers'),
      ]);
      setDashboard(dashRes.data);
      setContainers(tcRes.data || []);
    } catch (e) {
      console.error('Failed to load runtime overview:', e);
      setError(getErrorDetail(e, '加载运行时大盘失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContainerStats = async (containerId: string) => {
    const res = await apiClient.get(`/tenant-containers/${containerId}/stats`);
    return res.data;
  };

  const fetchContainerHistory = async (containerId: string, metric: string, minutes: number = 60) => {
    const res = await apiClient.get(`/tenant-containers/${containerId}/history?metric=${metric}&minutes=${minutes}`);
    return res.data;
  };

  const fetchContainerDiskUsage = async (containerId: string) => {
    const res = await apiClient.get(`/tenant-containers/${containerId}/disk-usage`);
    return res.data;
  };

  // 跨租户开关变化时在渲染期同步派生状态（替代原 effect 中的同步 setState）
  const showAllContainers = crossTenant && isSuperAdmin;
  const [prevShowAllContainers, setPrevShowAllContainers] = useState(showAllContainers);
  if (prevShowAllContainers !== showAllContainers) {
    setPrevShowAllContainers(showAllContainers);
    if (!showAllContainers) {
      setAllContainers(null);
    }
    setLoadingAllContainers(showAllContainers);
  }

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadData();
    };
    tick();
    // Probe super-admin capability by calling the super-admin-only system-config endpoint.
    apiClient
      .get('/system-config')
      .then(() => setIsSuperAdmin(true))
      .catch(() => setIsSuperAdmin(false));
  }, [loadData]);

  useEffect(() => {
    if (!showAllContainers) return;
    let cancelled = false;
    apiClient
      .get('/tenant-containers?all=true')
      .then((res) => {
        if (!cancelled) {
          setAllContainers(res.data || []);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(getErrorDetail(e, '加载跨租户容器列表失败'));
          setAllContainers([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingAllContainers(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showAllContainers, toast]);

  useEffect(() => {
    if (containers.length === 0) return;
    const loadStats = async () => {
      const stats: Record<string, ContainerStats> = {};
      await Promise.all(
        containers
          .filter((c) => c.status === 'running')
          .map(async (c) => {
            try {
              stats[c.id] = await fetchContainerStats(c.id);
            } catch {
              // ignore individual failures
            }
          })
      );
      setContainerStats(stats);
    };
    loadStats();
    const timer = setInterval(loadStats, 10000);
    return () => clearInterval(timer);
  }, [containers]);

  useEffect(() => {
    if (containers.length === 0) return;
    const loadDiskUsage = async () => {
      const usage: Record<string, ContainerDiskUsage> = {};
      await Promise.all(
        containers.map(async (c) => {
          try {
            usage[c.id] = await fetchContainerDiskUsage(c.id);
          } catch {
            // ignore individual failures
          }
        })
      );
      setContainerDiskUsage(usage);
    };
    loadDiskUsage();
  }, [containers]);

  useEffect(() => {
    if (containers.length === 0) return;
    const loadHistory = async () => {
      const history: Record<string, ContainerHistory> = {};
      await Promise.all(
        containers.map(async (c) => {
          try {
            const [cpu, mem] = await Promise.all([
              fetchContainerHistory(c.id, 'cpu', 60),
              fetchContainerHistory(c.id, 'memory', 60),
            ]);
            history[c.id] = { cpu, memory: mem };
          } catch {
            // ignore
          }
        })
      );
      setContainerHistory(history);
    };
    loadHistory();
  }, [containers]);

  const loadContainers = async () => {
    try {
      const res = await apiClient.get('/tenant-containers');
      setContainers(res.data || []);
    } catch (e) {
      console.error('Failed to load containers:', e);
      toast.error(getErrorDetail(e, '刷新容器列表失败'));
    }
  };

  const openMigrateModal = (runtime: RuntimeInContainer, sourceContainerId: string) => {
    setMigrateRuntime(runtime);
    setMigrateSourceContainerId(sourceContainerId);
    setMigrateTargetId('');
    setCrossTenant(false);
    setAllContainers(null);
  };

  const closeMigrateModal = () => {
    setMigrateRuntime(null);
    setMigrateSourceContainerId(null);
    setMigrateTargetId('');
    setMigrating(false);
    setCrossTenant(false);
    setAllContainers(null);
  };

  const submitMigrate = async () => {
    if (!migrateRuntime || !migrateTargetId) return;
    setMigrating(true);
    try {
      await apiClient.post(`/agent-runtimes/${migrateRuntime.id}/migrate`, {
        target_container_id: migrateTargetId,
        cross_tenant: crossTenant,
      });
      toast.success('迁移成功');
      closeMigrateModal();
      await loadContainers();
    } catch (e) {
      const detail = getErrorDetail(e, '迁移失败');
      toast.error(getErrorStatus(e) === 403 ? '无权限执行此操作' : detail);
    } finally {
      setMigrating(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const stopContainer = async (id: string) => {
    try {
      await apiClient.post(`/tenant-containers/${id}/stop`);
      toast.success('容器已停止');
      await loadContainers();
    } catch (e) {
      const detail = getErrorDetail(e, '停止容器失败');
      toast.error(getErrorStatus(e) === 403 ? '无权限执行此操作' : detail);
    }
  };

  const startContainer = async (id: string) => {
    try {
      await apiClient.post(`/tenant-containers/${id}/start`);
      toast.success('容器已启动');
      await loadContainers();
    } catch (e) {
      const detail = getErrorDetail(e, '启动容器失败');
      toast.error(getErrorStatus(e) === 403 ? '无权限执行此操作' : detail);
    }
  };

  const deleteContainer = async (id: string, name: string) => {
    if (!(await confirm({
      title: '确定删除该容器吗？',
      message: '容器内所有 Agent 运行时将被清理。',
      danger: true,
      requireText: name,
      confirmText: '删除',
    }))) {
      return;
    }
    try {
      await apiClient.delete(`/tenant-containers/${id}`);
      toast.success('容器已删除');
      await loadContainers();
    } catch (e) {
      const detail = getErrorDetail(e, '删除容器失败');
      toast.error(getErrorStatus(e) === 403 ? '无权限执行此操作' : detail);
    }
  };

  const scaleContainer = async (id: string, max_agents: number, resource_limit: object) => {
    await apiClient.post(`/tenant-containers/${id}/scale`, {
      max_agents,
      resource_limit,
    });
  };

  const openScaleModal = (tc: TenantContainer) => {
    setScaleTarget(tc);
    setScaleForm({
      max_agents: tc.max_agents,
      cpu: tc.resource_limit?.cpu !== undefined ? String(tc.resource_limit.cpu) : '',
      memory_mb: tc.resource_limit?.memory_mb !== undefined ? String(tc.resource_limit.memory_mb) : '',
    });
  };

  const closeScaleModal = () => {
    setScaleTarget(null);
    setScaling(false);
  };

  const submitScale = async () => {
    if (!scaleTarget) return;
    const max_agents = Number(scaleForm.max_agents);
    if (!Number.isInteger(max_agents) || max_agents < 1) {
      toast.error('请输入有效的 Agent 数量');
      return;
    }
    const resource_limit: Record<string, number> = {};
    if (scaleForm.cpu !== '') {
      const cpu = Number(scaleForm.cpu);
      if (Number.isNaN(cpu) || cpu <= 0) {
        toast.error('请输入有效的 CPU 限制');
        return;
      }
      resource_limit.cpu = cpu;
    }
    if (scaleForm.memory_mb !== '') {
      const memory_mb = Number(scaleForm.memory_mb);
      if (!Number.isInteger(memory_mb) || memory_mb <= 0) {
        toast.error('请输入有效的内存限制');
        return;
      }
      resource_limit.memory_mb = memory_mb;
    }
    setScaling(true);
    try {
      await scaleContainer(scaleTarget.id, max_agents, resource_limit);
      toast.success('容器扩容成功');
      closeScaleModal();
      await loadContainers();
    } catch (e) {
      const detail = getErrorDetail(e, '扩容失败');
      toast.error(getErrorStatus(e) === 403 ? '无权限执行此操作' : detail);
    } finally {
      setScaling(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    const map: Record<string, string> = {
      running: 'success',
      stopped: 'info',
      error: 'error',
      pending: 'warning',
      deploying: 'warning',
      created: 'secondary',
      offline: 'secondary',
      idle: 'info',
      full: 'warning',
    };
    return map[status] || 'info';
  };

  const getFrameworkName = (fw: string) => {
    const names: Record<string, string> = {
      celestial_toad: 'CT v4',
      hermes: 'Hermes',
      openclaw: 'OpenClaw',
      openhuman: 'OpenHuman',
      generic: '通用',
    };
    return names[fw] || fw;
  };

  const getErrorCount = () => {
    const containerErrors = containers.filter(
      (c) => c.status === 'error' || c.status === 'offline'
    ).length;
    const runtimeErrors = containers.reduce(
      (acc, c) => acc + c.runtimes.filter((r) => r.status === 'error' || r.status === 'offline').length,
      0
    );
    return containerErrors + runtimeErrors;
  };

  const totalRuntimeCount = containers.reduce((acc, c) => acc + c.runtimes.length, 0);
  const runningRuntimeCount = containers.reduce(
    (acc, c) => acc + c.runtimes.filter((r) => r.status === 'running').length,
    0
  );

  if (loading) {
    return (
      <div className="container">
        <div className="loading"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1>
          <BarChart3 size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          运行时大盘
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="secondary" onClick={() => navigate('/asset-runtimes')} title="查看资产在线运行时（人设 / 技能 / 知识 / MCP / 工作流）">
            <Globe size={16} /> Asset Runtime
          </button>
          <button className="secondary" onClick={() => navigate('/')}>
            <ArrowLeft size={16} /> 返回 Agent 列表
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-6 gap-4 mb-4">
        <div className="card text-center">
          <h2 style={{ fontSize: 28, margin: 0 }}>{containers.length}</h2>
          <p className="text-muted">容器总数</p>
        </div>
        <div className="card text-center" style={{ borderColor: 'var(--color-success)' }}>
          <h2 style={{ fontSize: 28, margin: 0, color: 'var(--color-success)' }}>
            {runningRuntimeCount}
          </h2>
          <p className="text-muted">运行中 Agent</p>
        </div>
        <div className="card text-center" style={{ borderColor: 'var(--color-info)' }}>
          <h2 style={{ fontSize: 28, margin: 0, color: 'var(--color-info)' }}>
            {totalRuntimeCount}
          </h2>
          <p className="text-muted">Agent 运行时总数</p>
        </div>
        <div className="card text-center" style={{ borderColor: 'var(--color-error)' }}>
          <h2 style={{ fontSize: 28, margin: 0, color: 'var(--color-error)' }}>
            {getErrorCount()}
          </h2>
          <p className="text-muted">异常</p>
        </div>
        <div className="card text-center">
          <h2 style={{ fontSize: 28, margin: 0 }}>{dashboard?.total_agents || 0}</h2>
          <p className="text-muted">Agent 总数</p>
        </div>
        <div className="card text-center">
          <h2 style={{ fontSize: 28, margin: 0 }}>{dashboard?.total_runtimes || 0}</h2>
          <p className="text-muted">全局运行时</p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="card mb-4" style={{ borderColor: 'var(--color-error)' }}>
          <div className="flex items-center gap-2" style={{ color: 'var(--color-error)' }}>
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Container cards */}
      <div className="card">
        <h3><Container size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />容器视图</h3>
        {containers.length === 0 ? (
          <div className="empty">
            <Server size={48} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p>暂无 TenantContainer</p>
            <p className="text-muted text-sm">容器将在 Agent 运行时部署后自动创建</p>
          </div>
        ) : (
          <div className="grid grid-3 gap-4" style={{ marginTop: 16 }}>
            {containers.map((tc) => (
              <div key={tc.id} className="card" style={{ padding: 16 }}>
                {/* Card header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
                    <Container size={18} />
                    <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tc.name}
                    </strong>
                  </div>
                  <span className={`badge ${getStatusBadgeClass(tc.status)}`}>{tc.status}</span>
                </div>

                {/* Meta */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="badge info">{getFrameworkName(tc.framework)}</span>
                  <span className="badge secondary">
                    {tc.agent_count}/{tc.max_agents} Agent
                  </span>
                </div>

                {tc.endpoint_url && (
                  <div className="text-sm text-muted mb-3" style={{ wordBreak: 'break-all' }}>
                    <ExternalLink size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {tc.endpoint_url}
                  </div>
                )}

                {tc.resource_limit && (
                  <div className="flex flex-wrap gap-3 mb-3 text-sm text-muted">
                    {tc.resource_limit.cpu !== undefined && (
                      <span className="flex items-center gap-1">
                        <Cpu size={12} /> CPU: {tc.resource_limit.cpu}
                      </span>
                    )}
                    {tc.resource_limit.memory_mb !== undefined && (
                      <span className="flex items-center gap-1">
                        <HardDrive size={12} /> 内存: {tc.resource_limit.memory_mb} MB
                      </span>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {tc.status === 'running' && (
                    <button
                      className="secondary"
                      title="停止"
                      onClick={() => stopContainer(tc.id)}
                    >
                      <Square size={14} /> 停止
                    </button>
                  )}
                  {(tc.status === 'offline' || tc.status === 'idle' || tc.status === 'error') && (
                    <button
                      className="secondary"
                      title="启动"
                      onClick={() => startContainer(tc.id)}
                    >
                      <Play size={14} /> 启动
                    </button>
                  )}
                  <button
                    className="secondary"
                    title="扩容"
                    onClick={() => openScaleModal(tc)}
                  >
                    <Maximize2 size={14} /> 扩容
                  </button>
                  <button
                    className="secondary"
                    title="删除"
                    style={{ color: 'var(--color-error)' }}
                    onClick={() => deleteContainer(tc.id, tc.name)}
                  >
                    <Trash2 size={14} /> 删除
                  </button>
                </div>

                {/* Resource usage */}
                {(containerStats[tc.id] || containerDiskUsage[tc.id]) && (
                  <div className="mt-3" style={{ padding: 12, background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)' }}>
                    {containerStats[tc.id] && (
                      <>
                        <ProgressBar label="CPU" percent={containerStats[tc.id].cpu_percent} value={containerStats[tc.id].cpu_percent.toFixed(1)} unit="%" />
                        <ProgressBar
                          label="内存"
                          percent={containerStats[tc.id].memory_percent}
                          value={`${containerStats[tc.id].memory_usage_mb}/${containerStats[tc.id].memory_limit_mb}`}
                          unit="MB"
                        />
                        <div className="text-muted text-sm">
                          进程数: {containerStats[tc.id].pids} | 网络 RX: {formatBytes(containerStats[tc.id].network_rx_bytes)} | TX: {formatBytes(containerStats[tc.id].network_tx_bytes)}
                        </div>
                      </>
                    )}
                    {containerDiskUsage[tc.id] && (
                      <DiskUsageBar
                        usage={containerDiskUsage[tc.id].usage_mb}
                        limit={containerDiskUsage[tc.id].limit_mb}
                        warningPercent={containerDiskUsage[tc.id].warning_percent}
                      />
                    )}
                    {containerHistory[tc.id] && (
                      <div className="mt-2">
                        <div className="text-sm mb-1">最近 1 小时趋势</div>
                        <div className="flex gap-4">
                          <div>
                            <div className="text-xs text-muted">CPU</div>
                            <Sparkline data={containerHistory[tc.id]?.cpu?.points} color="var(--color-primary)" />
                          </div>
                          <div>
                            <div className="text-xs text-muted">内存</div>
                            <Sparkline data={containerHistory[tc.id]?.memory?.points} color="var(--color-success)" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Toggle runtimes */}
                <button
                  className="secondary w-full"
                  style={{ justifyContent: 'center' }}
                  onClick={() => toggleExpand(tc.id)}
                >
                  {expanded[tc.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  {expanded[tc.id] ? '折叠 Agent 列表' : `展开 Agent 列表 (${tc.runtimes.length})`}
                </button>

                {/* Runtime list */}
                {expanded[tc.id] && (
                  <div className="list mt-3" style={{ marginTop: 12 }}>
                    {tc.runtimes.length === 0 ? (
                      <div className="empty text-sm">
                        <p>该容器暂无运行中的 Agent</p>
                      </div>
                    ) : (
                      tc.runtimes.map((rt) => (
                        <RuntimeItem
                          key={rt.id}
                          runtime={rt}
                          sourceContainerId={tc.id}
                          getStatusBadgeClass={getStatusBadgeClass}
                          getFrameworkName={getFrameworkName}
                          onViewAgent={(id) => navigate(`/agents/${id}/deployments`)}
                          onMigrate={openMigrateModal}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Framework breakdown from dashboard */}
      {dashboard && Object.keys(dashboard.framework_counts).length > 0 && (
        <div className="card mt-4">
          <h3>框架分布</h3>
          <div className="flex flex-wrap gap-2" style={{ marginTop: 12 }}>
            {Object.entries(dashboard.framework_counts).map(([fw, count]) => (
              <span key={fw} className="badge info">
                {getFrameworkName(fw)}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Scale modal */}
      {scaleTarget && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeScaleModal();
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 420 }}>
            <h3>
              <Maximize2 size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              扩容容器：{scaleTarget.name}
            </h3>
            <div className="form-group" style={{ marginTop: 16 }}>
              <label>最大 Agent 数</label>
              <input
                type="number"
                min={1}
                value={scaleForm.max_agents}
                onChange={(e) => setScaleForm((prev) => ({ ...prev, max_agents: Number(e.target.value) }))}
              />
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>CPU 限制（可选）</label>
              <input
                type="number"
                step="0.1"
                min={0}
                placeholder="例如 1.0"
                value={scaleForm.cpu}
                onChange={(e) => setScaleForm((prev) => ({ ...prev, cpu: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>内存限制 MB（可选）</label>
              <input
                type="number"
                min={0}
                placeholder="例如 1024"
                value={scaleForm.memory_mb}
                onChange={(e) => setScaleForm((prev) => ({ ...prev, memory_mb: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
              <button className="secondary" onClick={closeScaleModal} disabled={scaling}>
                取消
              </button>
              <button onClick={submitScale} disabled={scaling}>
                {scaling ? '扩容中...' : '确认扩容'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Migrate modal */}
      {migrateRuntime && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeMigrateModal();
          }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 420 }}>
            <h3>
              <Move size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
              迁移 AgentRuntime
            </h3>
            <p className="text-muted text-sm" style={{ marginTop: 8 }}>
              将 <strong>{migrateRuntime.agent_name}</strong> 迁移到{crossTenant ? '其他租户' : '同租户'}同框架的 RUNNING 容器
            </p>
            {isSuperAdmin && (
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={crossTenant}
                    onChange={(e) => {
                      setCrossTenant(e.target.checked);
                      setMigrateTargetId('');
                    }}
                  />
                  <span>跨租户迁移（super_admin）</span>
                </label>
              </div>
            )}
            <div className="form-group" style={{ marginTop: 16 }}>
              <label>目标容器</label>
              <select
                value={migrateTargetId}
                onChange={(e) => setMigrateTargetId(e.target.value)}
                disabled={crossTenant && loadingAllContainers}
              >
                <option value="">
                  {crossTenant && loadingAllContainers ? '加载跨租户容器中...' : '请选择目标容器'}
                </option>
                {(crossTenant ? allContainers || [] : containers)
                  .filter(
                    (c) =>
                      c.id !== migrateSourceContainerId &&
                      c.framework === migrateRuntime.framework &&
                      c.status === 'running' &&
                      c.agent_count < c.max_agents
                  )
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.agent_count}/{c.max_agents})
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
              <button className="secondary" onClick={closeMigrateModal} disabled={migrating}>
                取消
              </button>
              <button onClick={submitMigrate} disabled={migrating || !migrateTargetId}>
                {migrating ? '迁移中...' : '确认迁移'}
              </button>
            </div>
          </div>
        </div>
      )}
      {dialog}
    </div>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function Sparkline({ data, color, height = 40 }: { data: HistoryPoint[] | undefined; color: string; height?: number }) {
  if (!data || data.length < 2) return <div className="text-muted text-sm">暂无数据</div>;
  const width = 200;
  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = data
    .map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((d.value - min) / range) * height;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <polyline fill="none" stroke={color} strokeWidth={2} points={points} />
    </svg>
  );
}

function ProgressBar({ label, percent, value, unit }: { label: string; percent: number; value: string; unit: string }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span>{value} {unit} ({percent.toFixed(1)}%)</span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-tertiary)', borderRadius: 3, overflow: 'hidden' }}>
        <div
          style={{
            width: `${Math.min(100, Math.max(0, percent))}%`,
            height: '100%',
            background: percent > 80 ? 'var(--color-error)' : percent > 50 ? 'var(--color-warning)' : 'var(--color-success)',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}

function DiskUsageBar({ usage, limit, warningPercent }: { usage: number; limit: number; warningPercent: number }) {
  const percent = limit > 0 ? (usage / limit) * 100 : 0;
  const isWarning = percent >= warningPercent;
  return (
    <div style={{ marginBottom: 8, marginTop: 12 }}>
      <div className="flex justify-between text-sm">
        <span>磁盘</span>
        <span style={{ color: isWarning ? 'var(--color-error)' : undefined }}>
          {usage.toFixed(1)}/{limit} MB ({percent.toFixed(1)}%)
        </span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-tertiary)', borderRadius: 3, overflow: 'hidden' }}>
        <div
          style={{
            width: `${Math.min(100, Math.max(0, percent))}%`,
            height: '100%',
            background: isWarning ? 'var(--color-error)' : percent > 50 ? 'var(--color-warning)' : 'var(--color-success)',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}

interface RuntimeItemProps {
  runtime: RuntimeInContainer;
  sourceContainerId: string;
  getStatusBadgeClass: (status: string) => string;
  getFrameworkName: (fw: string) => string;
  onViewAgent: (agentId: string) => void;
  onMigrate: (runtime: RuntimeInContainer, sourceContainerId: string) => void;
}

function RuntimeItem({
  runtime,
  sourceContainerId,
  getStatusBadgeClass,
  getFrameworkName,
  onViewAgent,
  onMigrate,
}: RuntimeItemProps) {
  return (
    <div className="list-item" style={{ padding: 12 }}>
      <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="flex items-center gap-2">
          <strong>{runtime.agent_name}</strong>
          <span className={`badge ${getStatusBadgeClass(runtime.status)}`}>{runtime.status}</span>
          <span className="badge secondary">{getFrameworkName(runtime.framework)}</span>
          {runtime.port && <span className="badge info">:{runtime.port}</span>}
          {/* 镜像信息（P3-①）：当前镜像取自 runtime_info.image；固定镜像为钉定的 image_ref */}
          {runtime.runtime_info?.image != null && (
            <span
              className="badge secondary"
              title={`当前镜像：${String(runtime.runtime_info.image)}`}
              style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              当前镜像: {String(runtime.runtime_info.image)}
            </span>
          )}
          {runtime.image_ref && (
            <span
              className="badge info"
              title={`固定镜像：${runtime.image_ref}`}
              style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              固定镜像: {runtime.image_ref}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            className="secondary text-sm"
            title="迁移到其他容器"
            onClick={() => onMigrate(runtime, sourceContainerId)}
          >
            <Move size={14} /> 迁移
          </button>
          <button className="text-sm" onClick={() => onViewAgent(runtime.agent_id)}>
            查看 Agent
          </button>
        </div>
      </div>
      {runtime.endpoint_url && (
        <div className="text-sm text-muted mt-2" style={{ wordBreak: 'break-all' }}>
          {runtime.endpoint_url}
        </div>
      )}
    </div>
  );
}
